import { Entity } from '@backstage/catalog-model';

const CLUSTER_NAME_FOR_DATASOURCE_MAP: Record<string, { datasource: string }> =
  {
    'kaap-external-01-kaap-eastus-shared-01': {
      datasource: 'eetadzn87q1a8d',
    },
    'kaap-external-01-kaap-westeurope-odp-01': {
      datasource: 'df3pnt75b6l8ge',
    },
    'kaap-external-01-kaap-westeurope-shared-01': {
      datasource: '2c119bf1-84c8-16a6-fd3a-91bd28250676',
    },
    'kaap-internal-01-kaap-westeurope-shared-01': {
      datasource: '88e5c6b2-32e8-a063-efce-e6d98ddfbfb3',
    },
    'kaap-internal-01-kaap-westeurope-shared-02': {
      datasource: 'ce8rmxna67k74a',
    },
    'kaap-internal-01-kaap-koreacentral-shared-01': {
      datasource: 'eeojchmc22pkwd',
    },
    'kaap-internal-01-kaap-westeurope-odp-01': {
      datasource: 'feu9p2aww8c8wf',
    },
    'kaap-playground-01-kaap-westeurope-shared-03': {
      datasource: 'e34e8e2f-9fa7-071c-13ba-1bb330195859',
    },
    'kaap-internal-01-kaap-eastus-shared-01': {
      datasource: 'df3pnt75b6l8ge',
    },
  };

const buildGrafanaURL = (
  tenantName: string,
  clusterName: string,
  datasource: string,
) => {
  if (!tenantName || !datasource) return '';
  return `https://grafana.tomtomgroup.com/d/k8s_views_pods/kaap-customer-short-term-metrics?var-tenant=${encodeURIComponent(
    tenantName,
  )}&var-datasource=${encodeURIComponent(
    datasource,
  )}&var-cluster=${encodeURIComponent(clusterName)}`;
};

const buildGrafanaREDMetricsURL = (tenantName: string) => {
  if (!tenantName) return '';
  return 'https://grafana.tomtomgroup.com/d/fdnr1dhylh4w0c/kaap-metrics-red?var-tenant={TENANT_NAME}'.replace(
    '{TENANT_NAME}',
    encodeURIComponent(tenantName),
  );
};

const getClusterDomainURL = (kubeconfig: string) => {
  if (!kubeconfig) return '';
  const domain = /https:\/\/capsule-proxy\.(?<domain>[a-zA-Z0-9_.-]+)/.exec(
    kubeconfig,
  )?.groups?.domain;
  return domain;
};

const buildArgoCDURL = (kubeconfig: string) => {
  const domain = getClusterDomainURL(kubeconfig);
  if (!domain) {
    return '';
  }
  return `https://argocd.${domain}/`;
};

const getCostsURL = (dataSource: string, tenantName: string) => {
  if (!dataSource || !tenantName) return '';
  return `https://grafana.tomtomgroup.com/d/fdpqztyccc1s0a/tenant-cost-report?orgId=1&var-datasource=${dataSource}&var-tenant=${tenantName}`;
};

const buildAlertsURL = (kubeconfig: string, tenantName: string) => {
  if (!kubeconfig) return '';
  const domain = getClusterDomainURL(kubeconfig);
  return `https://alertmanager.${domain}/#/alerts?filter={namespace%3D~"kaap-${tenantName}-.*"}`;
};

const buildOtelURL = (clusterName: string, tenantName: string) => {
  if (!clusterName || !tenantName) return '';
  return `https://grafana.tomtomgroup.com/d/ddve8ix5h9szke/kaap-logs?var-cluster=${encodeURIComponent(
    clusterName,
  )}&var-tenant=${encodeURIComponent(tenantName)}`;
};

const getTrivyMetricsURL = (dataSource: string, tenantName: string) => {
  if (!dataSource || !tenantName) return '';
  return `https://grafana.tomtomgroup.com/d/ycwPj724k/kaap-security-scanning?orgId=1&var-datasource=${dataSource}&var-tenant=${tenantName}&var-namespace=All`;
};

const buildVMManagerURL = (kubeconfig: string) => {
  const domain = getClusterDomainURL(kubeconfig);
  return `https://vmalert.${domain}`;
};

const getTenantName = (entity: Entity): string => entity.metadata.name;

const getClusterId = (entity: Entity): string =>
  ((entity.spec?.tenant as any)?.clusterId as string) ?? '';

const getClusterName = (entity: Entity): string => {
  const clusterId = getClusterId(entity);
  return clusterId.split('/')[1] ?? '';
};

const getTier = (fetchedTenant: any): string => fetchedTenant?.tier ?? '';

const getKubeconfig = (fetchedTenant: any): string => {
  const kubeconfig = fetchedTenant?.kubeConfig;
  if (kubeconfig) {
    try {
      return atob(kubeconfig);
    } catch {
      return kubeconfig;
    }
  }
  return '';
};

const getSlackChannel = (entity: Entity, fetchedTenant: any): string =>
  ((entity.spec?.tenant as any)?.slackChannel as string) ??
  fetchedTenant?.slackChannel ??
  '';

export const getLink = (name: string, tenant: Entity, fetchedTenant: any) => {
  const tenantName = getTenantName(tenant);
  const clusterId = getClusterId(tenant);
  const clusterName = getClusterName(tenant);
  const tier = getTier(fetchedTenant ?? {});
  const kubeconfig = getKubeconfig(fetchedTenant);
  const slackChannel = getSlackChannel(tenant, fetchedTenant);
  const dataSourceKey = `kaap-${tier}-01-${clusterName}`;
  const linkMetadata = CLUSTER_NAME_FOR_DATASOURCE_MAP[dataSourceKey];

  switch (name) {
    case 'metrics':
      return buildGrafanaURL(
        tenantName,
        clusterId,
        linkMetadata?.datasource ?? '',
      );
    case 'redMetrics':
      return buildGrafanaREDMetricsURL(tenantName);
    case 'argo':
      return buildArgoCDURL(kubeconfig);
    case 'trivy':
      return getTrivyMetricsURL(linkMetadata?.datasource ?? '', tenantName);
    case 'costs':
      return getCostsURL(linkMetadata?.datasource ?? '', tenantName);
    case 'logs':
      return buildOtelURL(clusterName, tenantName);
    case 'alerts':
      return buildAlertsURL(kubeconfig, tenantName);
    case 'vmAlert':
      return buildVMManagerURL(kubeconfig);
    case 'slack':
      return `https://tomtomslack.slack.com/app_redirect?channel=${slackChannel}`;
    default:
      return '#';
  }
};
